import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, MapPin } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const PROFILE_IMAGE = "/profile.jpg";

/**
 * Hero – Premium entrance. Content is edited directly in this file.
 * Structure: status pill · avatar · name (oversized, split accent) · lead
 *            · meta chips · Primary+Secondary CTA · scroll indicator.
 */
export default function Hero() {
  const handleContactClick = (e) => {
    e.preventDefault();
    document
      .getElementById("links")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col items-start justify-center px-6 pt-32 pb-24 sm:px-10 md:px-16 lg:px-24"
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col items-start gap-8 md:gap-10">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 backdrop-blur-xl"
          data-testid="hero-status"
        >
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-[#CCFF00]" />
          <span className="font-mono-label text-[10px] text-zinc-400">
            Digital Nerd
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.05 }}
          className="relative"
          data-testid="profile-avatar-wrapper"
        >
          <div
            className="absolute -inset-3 rounded-full bg-gradient-to-tr from-[#FF4500]/60 via-[#FF4500]/10 to-[#CCFF00]/25 opacity-70 blur-2xl"
            aria-hidden="true"
          />
          <img
            src={PROFILE_IMAGE}
            alt="Ege Yıldız – Profilbild"
            width={160}
            height={160}
            loading="eager"
            decoding="async"
            className="relative h-24 w-24 rounded-full border border-white/10 object-cover shadow-[0_10px_40px_rgba(0,0,0,0.5)] md:h-32 md:w-32"
            data-testid="profile-avatar"
          />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.15 }}
          className="font-display -ml-0.5 text-6xl font-black leading-[0.85] tracking-tighter text-[#FAFAFA] sm:text-7xl md:text-8xl lg:text-[9.5rem]"
          data-testid="hero-name"
        >
          Ege
          <br />
          <span className="shimmer-text">Yıldız.</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
          className="max-w-2xl"
        >
          <p
            className="font-display text-2xl font-medium leading-snug tracking-tight text-[#FAFAFA] sm:text-3xl md:text-4xl"
            data-testid="hero-subtitle"
          >
            Technik-Fan, Bastler und{" "}
            <span className="text-zinc-500">digitaler Nerd.</span>
          </p>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-500 sm:text-lg">
            Meine persönliche Visitenkarte im Netz — was mich gerade
            beschäftigt, welche Tools ich liebe und wie du mich erreichst.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.42 }}
          className="flex flex-wrap items-center gap-2"
          data-testid="hero-meta"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 text-xs text-zinc-400">
            <MapPin size={12} strokeWidth={2} className="text-[#FF4500]" />
            Köln, DE
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 text-xs text-zinc-400">
            Smartphones · KI · Technik
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.55 }}
          className="mt-2 flex flex-wrap items-center gap-3"
        >
          <a
            href="#links"
            onClick={handleContactClick}
            data-testid="hero-contact-cta"
            className="group relative inline-flex items-center gap-2 rounded-full bg-[#FF4500] px-7 py-4 text-sm font-semibold tracking-wide text-white shadow-[0_10px_40px_rgba(255,69,0,0.28)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_16px_50px_rgba(255,69,0,0.5)]"
          >
            <span className="absolute inset-0 rounded-full bg-white opacity-0 mix-blend-overlay transition-opacity duration-300 group-hover:opacity-10" />
            Kontakt aufnehmen
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </a>
          <a
            href="#about"
            data-testid="hero-secondary-link"
            className="group inline-flex items-center gap-2 rounded-full px-5 py-4 text-sm font-medium text-zinc-400 transition-colors duration-300 hover:text-white"
          >
            Mehr über mich
            <ArrowDown
              size={14}
              className="transition-transform duration-300 group-hover:translate-y-0.5"
            />
          </a>
        </motion.div>
      </div>

      <motion.a
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        aria-label="Nach unten scrollen"
        data-testid="scroll-indicator"
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-zinc-500 transition-colors hover:text-zinc-200 sm:flex"
      >
        <span className="font-mono-label text-[10px]">Scroll</span>
        <ArrowDown size={16} className="scroll-bounce" />
      </motion.a>
    </section>
  );
}
